import { Plus, X } from "lucide-react";
import { usePlayground } from "../../contexts/PlaygroundContext";
import { MAX_USER_MESSAGES } from "./types";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { cn } from "../../lib/utils";

const textareaClass =
    "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none";

export default function PlaygroundInputArea() {
    const { state, setSystemPrompt, setUserMessage, addPrompt, removePrompt } = usePlayground();

    const isComparePrompts = state.mode === 'compare-prompts';
    const canAddMore = state.userMessages.length < MAX_USER_MESSAGES;
    const canRemove = state.userMessages.length > 1;

    if (isComparePrompts) {
        return (
            <TooltipProvider>
                <div className="flex flex-col gap-3">
                    <Label className="text-[13px] font-semibold">Prompts</Label>

                    {state.userMessages.map((userMessage, index) => (
                        <div
                            key={index}
                            className="flex flex-col gap-2.5 rounded-md border border-border/60 bg-muted/30 p-3.5"
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-primary">Prompt {index + 1}</span>
                                {canRemove && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                type="button"
                                                onClick={() => removePrompt(index)}
                                                disabled={state.isGenerating}
                                                aria-label="Remover prompt"
                                                className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>Remover prompt</TooltipContent>
                                    </Tooltip>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <span className="text-[11px] font-medium text-muted-foreground">System (opcional)</span>
                                <textarea
                                    rows={2}
                                    placeholder="Você é um assistente útil..."
                                    value={state.systemPrompts[index] || ''}
                                    onChange={(e) => setSystemPrompt(index, e.target.value)}
                                    disabled={state.isGenerating}
                                    className={cn(textareaClass)}
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <span className="text-[11px] font-medium text-muted-foreground">Mensagem do usuário</span>
                                <textarea
                                    rows={3}
                                    placeholder="Digite sua mensagem aqui..."
                                    value={userMessage}
                                    onChange={(e) => setUserMessage(index, e.target.value)}
                                    disabled={state.isGenerating}
                                    className={cn(textareaClass)}
                                />
                            </div>
                        </div>
                    ))}

                    {canAddMore && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={addPrompt}
                            disabled={state.isGenerating}
                            className="self-start"
                        >
                            <Plus className="h-4 w-4" />
                            Adicionar Prompt
                        </Button>
                    )}
                </div>
            </TooltipProvider>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
                <Label className="text-[13px] font-semibold">Prompt do Sistema (opcional)</Label>
                <textarea
                    rows={3}
                    placeholder="Você é um assistente útil e prestativo..."
                    value={state.systemPrompts[0] || ''}
                    onChange={(e) => setSystemPrompt(0, e.target.value)}
                    disabled={state.isGenerating}
                    className={cn(textareaClass)}
                />
            </div>
            <div className="flex flex-col gap-1.5">
                <Label className="text-[13px] font-semibold">Mensagem do Usuário</Label>
                <textarea
                    rows={5}
                    placeholder="Digite sua mensagem aqui..."
                    value={state.userMessages[0] || ''}
                    onChange={(e) => setUserMessage(0, e.target.value)}
                    disabled={state.isGenerating}
                    required
                    className={cn(textareaClass)}
                />
            </div>
        </div>
    );
}
